import { LoadingScreen } from 'src/components/loading-screen';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

// ----------------------------------------------------------------------

/**
 * A portal component that renders children into a native OS-level "Document Picture-in-Picture" window.
 * 
 * Note: The window must be opened by a user gesture in the parent component.
 */
export default function ChatCallPip({ 
  children, 
  pipWindow,
}) {
  const [isStylesLoaded, setIsStylesLoaded] = useState(false);

  /**
   * Copies all link tags and style tags from the main document to the PiP window.
   */
  const copyStyles = useCallback((targetWindow) => {
    // 1. Copy <link> tags (for external fonts, CSS files)
    const links = Array.from(document.querySelectorAll('link[rel="stylesheet"]'));
    links.forEach((link) => {
      const copy = targetWindow.document.createElement('link');
      copy.rel = 'stylesheet';
      copy.href = link.href;
      targetWindow.document.head.appendChild(copy);
    });

    // 2. Copy <style> tags (for JSS, Emotion, Styled Components)
    const styleSheets = Array.from(document.styleSheets);
    styleSheets.forEach((styleSheet) => {
      try {
        if (styleSheet.cssRules) {
          const newStyleEl = targetWindow.document.createElement('style');
          const rules = Array.from(styleSheet.cssRules)
            .map((rule) => rule.cssText)
            .join('');
          newStyleEl.textContent = rules;
          targetWindow.document.head.appendChild(newStyleEl);
        }
      } catch (e) {
        // Cross-origin styles handled by <link> tags
      }
    });

    // 3. Set basic background/font defaults for the new window
    targetWindow.document.body.style.margin = '0';
    targetWindow.document.body.style.padding = '0';
    targetWindow.document.body.style.backgroundColor = 'black'; 
    targetWindow.document.body.style.overflow = 'hidden';
    
    // 4. Give the browser a moment to parse the styles before we reveal the UI
    setTimeout(() => setIsStylesLoaded(true), 250);
  }, []);

  // Sync styles once when the portal is ready
  useEffect(() => {
    if (pipWindow) {
      // Small delay to ensure the window's document is settled
      const t = setTimeout(() => copyStyles(pipWindow), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [pipWindow, copyStyles]);

  // 1. If no pipWindow is provided, we are in "Normal/Docked" mode. 
  // We MUST render children normally to keep the component tree (and state/connection) alive.
  if (!pipWindow) return children;

  // 2. If we have a window but haven't loaded styles yet, show the loader in the new window.
  // We still use createPortal because we want this loader specifically in the PIP window.
  return createPortal(
    !isStylesLoaded ? (
      <div style={{ 
        width: '100vw', 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'black'
      }}>
        <LoadingScreen />
      </div>
    ) : children, 
    pipWindow.document.body
  );
}

ChatCallPip.propTypes = {
  children: PropTypes.node,
  pipWindow: PropTypes.object,
};
